/*  MUI  */
import {
    Form,
    FormFieldGroup,
    FormInput,
    FormInputHidden,
    FormModal,
    FormModalBody,
    FormModalCancelButton,
    FormModalFooter,
    FormModalSubmitButton,
    FormSelect,
    FormTextArea,
    components
} from '@auviknetworks/iceberg-ui';
import { useTheme } from '@mui/material/styles';
import { Notify } from 'application/Shared/Helpers';

/*  React  */
import React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * List of colors used for tags.
 */
const TAG_COLORS = [
    { labelKey: 'Colors.auvikLavender', value: 'option2' },
    { labelKey: 'Colors.auvikCyan', value: 'option1' },
    { labelKey: 'Colors.auvikPink', value: 'option6' },
    { labelKey: 'Colors.auvikYellow', value: 'option7' },
    { labelKey: 'Colors.auvikOrange', value: 'option3' },
    { labelKey: 'Colors.auvikGreen', value: 'option4' },
    { labelKey: 'Colors.auvikBrown', value: 'option5' },
    { labelKey: 'Colors.auvikSkyBlue', value: 'option8' }
];

/**
 * Custom React-Select Option component that renders an additional color circle
 * next to the option label.
 *
 * react-select documentation for Components: https://react-select.com/components
 *
 * @param {Object} props - The props object passed to the component.
 * @param {string} props.children - The label text for the option.
 * @param {Object} props.data - The option data object.
 * @param {string} props.data.value - The color value for the option.
 * @returns {JSX.Element} The rendered custom option component.
 */
export const CustomOption = (props) => (
    <components.Option {...props}>
        <span
            data-testid="colorSwatch"
            style={{
                backgroundColor: props.data.value,
                borderRadius: '25%',
                display: 'inline-block',
                height: '10px',
                marginRight: '8px',
                width: '10px'
            }}
        />
        {props.children}
    </components.Option>
);

/**
 * A form component for creating or editing a tag.
 *
 * @param {Object} props - The props object passed to the component.
 * @param {Object|null} props.defaultValues - The initial values for the form fields, or empty Object for creating a new tag.
 * @param {Object} props.fields - An object containing field configuration for the form fields. Each field can have a readOnly property.
 * @param {boolean} props.disabled - Whether the form fields should be disabled.
 * @param {Function} props.handleClose - The function to call when the form is closed.
 * @param {Function} props.handleSubmit - The function to call when the form is submitted with the form data.
 * @param {boolean} props.isOpen - Whether the form modal should be open or closed.
 * @param {string} props.title - The title to display in the form modal.
 * @returns {JSX.Element} The rendered tag form component.
 */
export const TagForm = ({
    defaultValues,
    fields,
    disabled,
    handleClose,
    handleSubmit,
    isOpen,
    title
}) => {
    const { t } = useTranslation();
    const theme = useTheme();

    const validate = (values) => {
        const results = { errors: {}, values };

        if (!values.name)
            results.errors.name = {
                message: t('Form.fieldRequired'),
                type: 'required'
            };

        return results;
    };

    return (
        <FormModal title={title} closeModal={handleClose} isOpen={isOpen}>
            <Form
                defaultValues={defaultValues}
                onSubmit={async (values, setError) => {
                    try {
                        await handleSubmit(values);

                        Notify.success(t('TagsForm.success', values));
                        handleClose();
                    } catch (exception) {
                        Notify.error(t('TagsForm.server.error', values));

                        setError('root.serverError', {
                            type: exception.response.statusCode
                        });
                    }
                }}
                validate={validate}
            >
                <FormModalBody>
                    <FormFieldGroup disabled={disabled}>
                        <FormInputHidden name="id" />
                        <FormInput
                            name="name"
                            label={t('Common.name')}
                            placeholder={t('TagsForm.fields.name.placeholder')}
                            required
                            readOnly={fields?.name?.readOnly}
                        />
                        <FormSelect
                            label={t('Common.color')}
                            name="color"
                            components={{ Option: CustomOption }}
                            options={TAG_COLORS.map(({ labelKey, value }) => ({
                                label: t(labelKey),
                                value: theme.palette.tag[value]
                            }))}
                            width="100%"
                            readOnly={fields?.color?.readOnly}
                        />
                        <FormTextArea
                            name="description"
                            label={t('Common.description')}
                            width="100%"
                            disareadOnlybled={fields?.description?.readOnly}
                        />
                    </FormFieldGroup>
                </FormModalBody>
                <FormModalFooter>
                    <FormModalCancelButton>
                        {t('Common.cancel')}
                    </FormModalCancelButton>
                    <FormModalSubmitButton>
                        {t('Common.save')}
                    </FormModalSubmitButton>
                </FormModalFooter>
            </Form>
        </FormModal>
    );
};
